import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLesson, completeLesson, Content, Lesson } from '../services/api';
import WordCard from '../components/WordCard';
import { speak } from '../services/audio';
import './LessonView.css';

type ViewMode = 'overview' | 'word-by-word' | 'self-eval' | 'completed';

// Track word state for self-evaluation
interface WordState {
  id: string;
  excluded: boolean;
  hardLock: number; // iterations remaining where "Łatwizna" is hidden
}

const LessonView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<(Lesson & { content: Content[] }) | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pointsEarned, setPointsEarned] = useState(0);

  // Self-evaluation state
  const [wordStates, setWordStates] = useState<Map<string, WordState>>(new Map());
  const [selfEvalIndex, setSelfEvalIndex] = useState(0);

  // Challenge modal state
  const [showChallenge, setShowChallenge] = useState(false);
  const [challengeInput, setChallengeInput] = useState('');
  const [challengeAttempts, setChallengeAttempts] = useState(0);
  const [challengeFeedback, setChallengeeFeedback] = useState<'none' | 'success' | 'retry' | 'fail'>('none');

  useEffect(() => {
    const loadLesson = async () => {
      if (!id) return;
      try {
        const data = await getLesson(id);
        setLesson(data);
      } catch (err) {
        console.error('Failed to load lesson:', err);
      }
    };
    loadLesson();
  }, [id]);

  const wordCount = lesson?.content.filter(c => c.type === 'word').length || 0;
  const sentenceCount = lesson?.content.filter(c => c.type === 'sentence').length || 0;

  const startWordByWord = () => {
    setCurrentIndex(0);
    setViewMode('word-by-word');
    if (lesson && lesson.content.length > 0) {
      speak(lesson.content[0].english);
    }
  };

  const startSelfEval = () => {
    if (!lesson) return;
    // Initialize word states
    const states = new Map<string, WordState>();
    lesson.content.forEach(c => {
      states.set(c.id, { id: c.id, excluded: false, hardLock: 0 });
    });
    setWordStates(states);
    setSelfEvalIndex(0);
    setViewMode('self-eval');
    if (lesson.content.length > 0) {
      speak(lesson.content[0].english);
    }
  };

  // Get active (non-excluded) words for self-eval
  const getActiveWords = () => {
    if (!lesson) return [];
    return lesson.content.filter(c => {
      const state = wordStates.get(c.id);
      return !state || !state.excluded;
    });
  };

  const handleEasyClick = () => {
    // 30% chance to show challenge
    if (Math.random() < 0.3) {
      setShowChallenge(true);
      setChallengeInput('');
      setChallengeAttempts(0);
      setChallengeeFeedback('none');
    } else {
      handleSelfEvalNext('easy');
    }
  };

  const handleChallengeSubmit = () => {
    if (!lesson) return;
    const activeWords = getActiveWords();
    const currentWord = activeWords[selfEvalIndex % activeWords.length];

    const normalize = (s: string) => s.trim().replace(/[.!?]+$/, '').toLowerCase();
    if (normalize(challengeInput) === normalize(currentWord.english)) {
      // Correct answer
      setChallengeeFeedback('success');
      setTimeout(() => {
        setShowChallenge(false);
        handleSelfEvalNext('easy');
      }, 1000);
    } else {
      // Wrong answer
      const newAttempts = challengeAttempts + 1;
      setChallengeAttempts(newAttempts);

      if (newAttempts >= 2) {
        // Failed after 2 attempts
        setChallengeeFeedback('fail');
      } else {
        // First wrong attempt
        setChallengeeFeedback('retry');
      }
    }
  };

  const handleSelfEvalNext = (action: 'easy' | 'ok' | 'hard') => {
    if (!lesson) return;
    const activeWords = getActiveWords();
    if (activeWords.length === 0) return;

    const currentWord = activeWords[selfEvalIndex % activeWords.length];
    const newStates = new Map(wordStates);
    const currentState = newStates.get(currentWord.id) || { id: currentWord.id, excluded: false, hardLock: 0 };

    if (action === 'easy' && currentState.hardLock === 0) {
      // Exclude word
      newStates.set(currentWord.id, { ...currentState, excluded: true });
    } else if (action === 'hard') {
      // Lock for 2 more iterations
      newStates.set(currentWord.id, { ...currentState, hardLock: 2 });
    } else if (action === 'ok') {
      // Decrease hard lock if any
      if (currentState.hardLock > 0) {
        newStates.set(currentWord.id, { ...currentState, hardLock: currentState.hardLock - 1 });
      }
    }

    setWordStates(newStates);

    // Check if all words are excluded
    const remainingWords = lesson.content.filter(c => {
      const state = newStates.get(c.id);
      return !state || !state.excluded;
    });

    if (remainingWords.length === 0) {
      handleComplete();
      return;
    }

    // Move to next word (circular)
    const nextIndex = (selfEvalIndex + 1) % remainingWords.length;
    setSelfEvalIndex(nextIndex);
    speak(remainingWords[nextIndex].english);
  };

  const handleNext = () => {
    if (!lesson) return;
    if (currentIndex < lesson.content.length - 1) {
      setCurrentIndex(currentIndex + 1);
      speak(lesson.content[currentIndex + 1].english);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      if (lesson) speak(lesson.content[currentIndex - 1].english);
    }
  };

  const handleComplete = async () => {
    if (!id) return;
    try {
      const result = await completeLesson(id);
      setPointsEarned(result.pointsEarned);
      setViewMode('completed');
    } catch (err) {
      console.error('Failed to complete lesson:', err);
    }
  };

  if (!lesson) {
    return <div className="loading">Ładowanie...</div>;
  }

  if (!lesson.content || lesson.content.length === 0) {
    return (
      <div className="lesson-view fade-in">
        <div className="lesson-header">
          <h2>{lesson.name_pl}</h2>
        </div>
        <div className="empty-lesson">
          <div className="empty-icon">🚧</div>
          <h3>Ta lekcja jest w przygotowaniu</h3>
          <p>Wkrótce dodamy tutaj nowe słówka i zdania!</p>
          <button className="btn btn-primary btn-large" onClick={() => navigate('/lessons')}>
            Wróć do lekcji
          </button>
        </div>
      </div>
    );
  }

  // Completed view
  if (viewMode === 'completed') {
    return (
      <div className="lesson-complete fade-in">
        <div className="complete-icon">🎉</div>
        <h1>Świetnie!</h1>
        <p>Ukończyłeś lekcję "{lesson.name_pl}"</p>
        <div className="points-earned bounce">
          <span>+{pointsEarned}</span> punktów!
        </div>
        <div className="complete-actions">
          <button className="btn btn-primary btn-large" onClick={() => navigate('/lessons')}>
            Wróć do lekcji
          </button>
          <button className="btn btn-secondary btn-large" onClick={() => navigate('/games')}>
            Zagraj w grę 🎮
          </button>
        </div>
      </div>
    );
  }

  // Self-evaluation view
  if (viewMode === 'self-eval') {
    const activeWords = getActiveWords();
    if (activeWords.length === 0) {
      handleComplete();
      return null;
    }
    const currentWord = activeWords[selfEvalIndex % activeWords.length];
    const currentState = wordStates.get(currentWord.id);
    const isHardLocked = currentState && currentState.hardLock > 0;
    const excludedCount = lesson.content.length - activeWords.length;

    return (
      <div className="lesson-view fade-in">
        <div className="lesson-header">
          <button className="btn btn-secondary btn-small" onClick={() => setViewMode('overview')}>
            ← Wróć
          </button>
          <h2>{lesson.name_pl}</h2>
          <div className="progress-indicator">
            {excludedCount} / {lesson.content.length} ✓
          </div>
        </div>

        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(excludedCount / lesson.content.length) * 100}%` }}
          />
        </div>

        <div className={`card-container ${showChallenge ? 'blurred' : ''}`}>
          <WordCard content={currentWord} size="large" />
        </div>

        {showChallenge && (
          <div className="challenge-modal">
            <div className="challenge-content">
              <h3>Czy oby na pewno łatwizna?</h3>
              <p>Napisz po angielsku: <strong>{currentWord.polish}</strong></p>
              <input
                type="text"
                className="challenge-input"
                value={challengeInput}
                onChange={(e) => setChallengeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleChallengeSubmit()}
                autoFocus
                disabled={challengeFeedback === 'success' || challengeFeedback === 'fail'}
              />
              {challengeFeedback === 'none' && (
                <button className="btn btn-primary" onClick={handleChallengeSubmit}>
                  Sprawdź
                </button>
              )}
              {challengeFeedback === 'success' && (
                <p className="challenge-feedback success">✅ Nieźle!</p>
              )}
              {challengeFeedback === 'retry' && (
                <>
                  <p className="challenge-feedback retry">❌ Nie do końca - spróbuj poprawić</p>
                  <button className="btn btn-primary" onClick={handleChallengeSubmit}>
                    Sprawdź ponownie
                  </button>
                </>
              )}
              {challengeFeedback === 'fail' && (
                <>
                  <p className="challenge-feedback fail">
                    😔 Niestety, trzeba jeszcze poćwiczyć! Słowo, którego szukasz to: <strong>{currentWord.english}</strong>
                  </p>
                  <button
                    className="btn btn-secondary challenge-close-btn"
                    onClick={() => {
                      setShowChallenge(false);
                      handleSelfEvalNext('ok');
                    }}
                  >
                    No dobra...
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {!showChallenge && (
          <div className="self-eval-box">
            <p className="self-eval-prompt">Czy znasz to słówko?</p>
            <div className="self-eval-buttons">
              {!isHardLocked && (
                <button
                  className="btn self-eval-btn easy"
                  onClick={handleEasyClick}
                >
                  ✅ Łatwizna!
                </button>
              )}
              <button
                className="btn self-eval-btn ok"
                onClick={() => handleSelfEvalNext('ok')}
              >
                🤔 Dobra, chyba pamiętam
              </button>
              <button
                className="btn self-eval-btn hard"
                onClick={() => handleSelfEvalNext('hard')}
              >
                😰 Oj, trudne!
              </button>
            </div>
            <p className="self-eval-remaining">Pozostało: {activeWords.length} słów</p>
          </div>
        )}
      </div>
    );
  }

  // Word-by-word learning view
  if (viewMode === 'word-by-word') {
    const currentContent = lesson.content[currentIndex];
    return (
      <div className="lesson-view fade-in">
        <div className="lesson-header">
          <button className="btn btn-secondary btn-small" onClick={() => setViewMode('overview')}>
            ← Wróć
          </button>
          <h2>{lesson.name_pl}</h2>
          <div className="progress-indicator">
            {currentIndex + 1} / {lesson.content.length}
          </div>
        </div>

        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${((currentIndex + 1) / lesson.content.length) * 100}%` }}
          />
        </div>

        <div className="card-container">
          <WordCard content={currentContent} size="large" />
        </div>

        <div className="lesson-controls">
          <button
            className="btn btn-secondary"
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            ⬅️ Poprzednie
          </button>
          <button className="btn btn-primary btn-large" onClick={handleNext}>
            {currentIndex === lesson.content.length - 1 ? 'Zakończ ✅' : 'Następne ➡️'}
          </button>
        </div>
      </div>
    );
  }

  // Overview view (default)
  return (
    <div className="lesson-view fade-in">
      <div className="lesson-header">
        <button className="btn btn-secondary btn-small" onClick={() => navigate('/lessons')}>
          ← Wróć
        </button>
        <h2>{lesson.name_pl}</h2>
      </div>

      <div className="lesson-stats">
        <span className="stat-item">📝 {wordCount} słów</span>
        <span className="stat-item">💬 {sentenceCount} zdań</span>
      </div>

      <div className="learning-methods">
        <h3>Metody nauki</h3>
        <div className="methods-grid">
          <button className="method-tile" onClick={startWordByWord}>
            <span className="method-icon">📖</span>
            <span className="method-name">Słowo po słowie</span>
            <span className="method-desc">Przeglądaj słówka jedno po drugim</span>
          </button>
          <button className="method-tile" onClick={startSelfEval}>
            <span className="method-icon">🧠</span>
            <span className="method-name">Samosprawdzian</span>
            <span className="method-desc">Oceń swoją znajomość słówek</span>
          </button>
        </div>
      </div>

      <div className="lesson-content-list">
        <h3>Zawartość lekcji</h3>
        <div className="content-list">
          {[...lesson.content].sort((a, b) => {
            if (a.type !== b.type) return a.type === 'word' ? -1 : 1;
            return a.english.localeCompare(b.english);
          }).map((item, idx) => (
            <div key={idx} className={`content-item ${item.type}`}>
              <span className="content-english">{item.english}</span>
              <span className="content-polish">{item.polish}</span>
              <button
                className="speak-btn-small"
                onClick={() => speak(item.english)}
                title="Posłuchaj"
              >
                🔊
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LessonView;

